import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Rocket, LayoutGrid, Microscope, Database } from "lucide-react";
import { useGetPath } from "@/hooks/useGetPath";
import { useHideOnScroll } from "@/hooks/useHideOnScroll";
import { MobileBottomNavSlotSetterContext, MobileBottomNavVisibilityContext } from "@/contexts/MobileSidebarContext";

// Icons and labels mirror the left nav's playground section (see NewSideBar) so
// the same destination reads the same wherever it's reached from. The old
// "More" burger is gone — the top bar owns opening the nav now — and Datasets
// takes the fourth slot instead.
type ChipNotch = { cx: number; top: number; width: number; height: number };

type NavFit = { slot: number; scale: number };

/**
 * Center slot for the event chip. Wide phones keep the chip at full size and
 * give the leftover width to the side labels. Narrow phones shrink the chip
 * so each side item keeps enough room that its label stays inside its column.
 */
function workbenchNavFit(barWidth: number, chipWidth: number, chipHeight: number): NavFit {
  const halo = Math.max(8, chipHeight * 0.25);
  const chip = Math.max(chipWidth, 1);
  const minItem = 52;
  const maxSlot = Math.max(88, barWidth - minItem * 4);
  const desired = chip + halo * 2;
  if (desired <= maxSlot) return { slot: desired, scale: 1 };
  const scale = Math.min(1, (maxSlot - halo * 2) / chip);
  return { slot: maxSlot, scale: Math.max(scale, 0.01) };
}

/**
 * Bar outline whose center cutout is the event chip, with a clear gap around it.
 * `open` grows that cutout from a plain rounded bar (0) to the full pill (1).
 * The ends are the same semicircles as the pill. A short fillet meets the top
 * edge. Sweep flags are the ones whose arc center is the pill cap — the other
 * flag bulges away from the chip.
 */
function workbenchNavPath(width: number, height: number, chip: ChipNotch, open = 1) {
  const radius = Math.min(16, height / 2 - 1, width / 4);
  const n = (value: number) => Math.round(value * 100) / 100;
  if (open <= 0.02) {
    return [
      `M ${n(radius)} 0`,
      `H ${n(width - radius)}`,
      `A ${radius} ${radius} 0 0 1 ${n(width)} ${radius}`,
      `V ${n(height - radius)}`,
      `A ${radius} ${radius} 0 0 1 ${n(width - radius)} ${n(height)}`,
      `H ${n(radius)}`,
      `A ${radius} ${radius} 0 0 1 0 ${n(height - radius)}`,
      `V ${n(radius)}`,
      `A ${radius} ${radius} 0 0 1 ${n(radius)} 0`,
      "Z",
    ].join(" ");
  }
  const shownHeight = chip.height * open;
  const shownWidth = chip.width * open;
  const cy = chip.top + chip.height / 2;
  // Same clearance as the original 32px chip (8px gap, 4px fillet), scaled with the pill.
  const gap = shownHeight * 0.25;
  const fillet = Math.max(1, shownHeight * 0.125);
  const chipRadius = shownHeight / 2;
  const R = chipRadius + gap;
  const flat = Math.max(0, shownWidth / 2 + gap - R);
  const bottomY = cy + R;

  const side = (capX: number, sign: -1 | 1) => {
    const reach = Math.sqrt(Math.max(0, (R + fillet) ** 2 - (fillet - cy) ** 2));
    const fx = capX + sign * reach;
    const scale = R / (R + fillet);
    return {
      fx,
      x: capX + scale * sign * reach,
      y: cy + scale * (fillet - cy),
    };
  };
  const left = side(chip.cx - flat, -1);
  const right = side(chip.cx + flat, 1);

  return [
    `M ${n(radius)} 0`,
    `H ${n(left.fx)}`,
    `A ${fillet} ${fillet} 0 0 1 ${n(left.x)} ${n(left.y)}`,
    `A ${n(R)} ${n(R)} 0 0 0 ${n(chip.cx - flat)} ${n(bottomY)}`,
    `H ${n(chip.cx + flat)}`,
    `A ${n(R)} ${n(R)} 0 0 0 ${n(right.x)} ${n(right.y)}`,
    `A ${fillet} ${fillet} 0 0 1 ${n(right.fx)} 0`,
    `H ${n(width - radius)}`,
    `A ${radius} ${radius} 0 0 1 ${n(width)} ${radius}`,
    `V ${n(height - radius)}`,
    `A ${radius} ${radius} 0 0 1 ${n(width - radius)} ${n(height)}`,
    `H ${n(radius)}`,
    `A ${radius} ${radius} 0 0 1 0 ${n(height - radius)}`,
    `V ${n(radius)}`,
    `A ${radius} ${radius} 0 0 1 ${n(radius)} 0`,
    "Z",
  ].join(" ");
}

const navItems = [
  { id: "uploads", href: "/", name: "Start", icon: Rocket },
  { id: "examples", href: "/examples", name: "Examples", icon: LayoutGrid },
  { id: "events", href: "/events", name: "Analyze", icon: Microscope },
  { id: "dataset-marketplace", href: "/dataset-studio", name: "Dataset Studio", compactName: "Datasets", icon: Database },
];

const BottomNavigation: React.FC<{ forceHidden?: boolean }> = ({ forceHidden = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const getPath = useGetPath();
  // Hide while scrolling down so content gets the full viewport; reappear on
  // scroll up. Desktop (lg+) never mounts this chrome via `lg:hidden`.
  const hideOnScroll = useHideOnScroll();
  const setBottomNavSlot = React.useContext(MobileBottomNavSlotSetterContext);
  const { setHidden: setBottomNavHidden } = React.useContext(MobileBottomNavVisibilityContext);
  React.useEffect(() => {
    setBottomNavHidden(hideOnScroll || forceHidden);
  }, [hideOnScroll, forceHidden, setBottomNavHidden]);
  React.useEffect(() => () => setBottomNavHidden(false), [setBottomNavHidden]);
  const barRef = React.useRef<HTMLDivElement>(null);
  const slotRef = React.useRef<HTMLDivElement | null>(null);
  const datasetButtonRef = React.useRef<HTMLButtonElement>(null);
  const [barSize, setBarSize] = React.useState({ width: 0, height: 0 });
  const [chipNotch, setChipNotch] = React.useState<ChipNotch | null>(null);
  const [notchOpen, setNotchOpen] = React.useState(0);
  const chipReady = chipNotch !== null;
  const [compactDatasetLabel, setCompactDatasetLabel] = React.useState(false);
  const [navFit, setNavFit] = React.useState<NavFit | null>(null);

  const normalizedPath = location.pathname.startsWith("/live-demo")
    ? location.pathname.slice("/live-demo".length) || "/"
    : location.pathname;
  const workbench = /^\/workbench\//.test(normalizedPath);

  const setSlot = React.useCallback(
    (node: HTMLDivElement | null) => {
      slotRef.current = node;
      setBottomNavSlot(node);
    },
    [setBottomNavSlot],
  );

  React.useLayoutEffect(() => {
    const bar = barRef.current;
    const slot = slotRef.current;
    if (!workbench || !bar || !slot) {
      setChipNotch(null);
      setNavFit(null);
      return;
    }
    let chip: Element | null = null;
    const measure = () => {
      const width = bar.clientWidth;
      const height = bar.clientHeight;
      setBarSize((prev) => (prev.width === width && prev.height === height ? prev : { width, height }));
      if (!(chip instanceof HTMLElement) || width <= 0 || height <= 0) {
        setChipNotch(null);
        return;
      }
      const barRect = bar.getBoundingClientRect();
      const chipRect = chip.getBoundingClientRect();
      if (chipRect.width <= 0 || chipRect.height <= 0) {
        setChipNotch(null);
        return;
      }
      const next: ChipNotch = {
        cx: chipRect.left - barRect.left + chipRect.width / 2,
        top: chipRect.top - barRect.top,
        width: chipRect.width,
        height: chipRect.height,
      };
      setChipNotch((prev) =>
        prev &&
        Math.abs(prev.cx - next.cx) < 0.5 &&
        Math.abs(prev.top - next.top) < 0.5 &&
        Math.abs(prev.width - next.width) < 0.5 &&
        Math.abs(prev.height - next.height) < 0.5
          ? prev
          : next,
      );
      const fit = workbenchNavFit(width, chip.offsetWidth, chip.offsetHeight);
      const scaleText = fit.scale.toFixed(3);
      const transform =
        fit.scale < 0.995
          ? `translateX(-50%) translateY(calc(-50% - 12.5%)) scale(${scaleText})`
          : "";
      if (chip.style.transform !== transform) chip.style.transform = transform;
      setNavFit((prev) =>
        prev && Math.abs(prev.slot - fit.slot) < 0.5 && Math.abs(prev.scale - fit.scale) < 0.01
          ? prev
          : fit,
      );
    };
    const watchChip = () => {
      const next = slot.firstElementChild;
      if (next !== chip) {
        if (chip) resize.unobserve(chip);
        chip = next;
        if (chip) resize.observe(chip);
      }
      measure();
    };
    const resize = new ResizeObserver(measure);
    // Class and style changes move the chip without resizing it.
    const mutation = new MutationObserver(watchChip);
    resize.observe(bar);
    mutation.observe(slot, { childList: true, subtree: true, attributes: true, attributeFilter: ["class", "style"] });
    watchChip();
    return () => {
      resize.disconnect();
      mutation.disconnect();
    };
  }, [workbench]);

  React.useEffect(() => {
    if (!chipReady) {
      setNotchOpen(0);
      return;
    }
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setNotchOpen(1);
      return;
    }
    let start = 0;
    let frame = 0;
    const step = (now: number) => {
      if (!start) start = now;
      const t = Math.min(1, (now - start) / 340);
      setNotchOpen(1 - (1 - t) ** 3);
      if (t < 1) frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [chipReady]);

  React.useLayoutEffect(() => {
    const button = datasetButtonRef.current;
    if (!button) return;
    const label = button.querySelector("span:last-child");
    const style = getComputedStyle(label ?? button);
    const probe = document.createElement("span");
    probe.textContent = "Dataset Studio";
    probe.style.cssText = `position:absolute;visibility:hidden;white-space:nowrap;font:${style.fontWeight} ${style.fontSize} ${style.fontFamily};letter-spacing:${style.letterSpacing};`;
    document.body.appendChild(probe);
    const needed = probe.getBoundingClientRect().width;
    probe.remove();
    const measure = () => setCompactDatasetLabel(needed > button.clientWidth - 4);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(button);
    return () => observer.disconnect();
  }, [workbench, normalizedPath]);

  const isActive = (href: string) => {
    if (href === "/") {
      return normalizedPath === "/" || normalizedPath === "/start-page";
    }
    return normalizedPath.startsWith(href);
  };

  return (
    // Floats above the page rather than sitting flush: inset from all three
    // edges, fully rounded, one hairline border — same surface color as the
    // top bar and the left nav. Slides off-screen below its bottom inset when
    // the user scrolls down.
    <div
      hidden={forceHidden}
      className={`fixed bottom-3 left-3 right-3 z-50 overflow-visible transition-[transform,opacity] duration-300 ease-out will-change-transform lg:hidden ${
        hideOnScroll
          ? "translate-y-[calc(100%+0.75rem)] opacity-0 pointer-events-none"
          : "translate-y-0 opacity-100"
      }`}
      aria-hidden={hideOnScroll || forceHidden || undefined}
    >
      <style>{`
        @keyframes phoneNavChipIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .phone-nav-chip { animation: phoneNavChipIn 340ms cubic-bezier(0.22, 1, 0.36, 1) both; }
        @media (prefers-reduced-motion: reduce) {
          .phone-nav-chip { animation: none; }
        }
      `}</style>
      {workbench && barSize.width > 0 && barSize.height > 0 && chipNotch ? (
        <svg
          aria-hidden
          className="pointer-events-none absolute inset-0 h-full w-full overflow-visible"
          viewBox={`0 0 ${barSize.width} ${barSize.height}`}
          style={{ filter: "drop-shadow(0 4px 12px rgba(17,19,35,0.08))" }}
        >
          <path
            d={workbenchNavPath(barSize.width, barSize.height, chipNotch, notchOpen)}
            fill="#FCFAF4"
            stroke="#e2dccd"
            strokeWidth="1"
          />
        </svg>
      ) : (
        <div className="pointer-events-none absolute inset-0 rounded-2xl border border-[#e2dccd] bg-[#FCFAF4] shadow-[0_4px_16px_rgba(17,19,35,0.08)]" />
      )}
      <div ref={barRef} className="relative z-[2] flex items-start justify-around py-1">
        {(workbench ? [navItems.slice(0, 2), navItems.slice(2)] : [navItems]).map((group, groupIndex) => (
          <React.Fragment key={groupIndex}>
            {groupIndex === 1 ? (
              <div
                ref={setSlot}
                className="relative w-[min(11rem,calc(100%-12rem))] shrink-0 self-stretch"
                style={navFit ? { width: navFit.slot } : undefined}
              />
            ) : null}
            {group.map((item) => {
              const IconComponent = item.icon;
              const active = isActive(item.href);
              const compact = item.compactName != null && compactDatasetLabel;
              const label = compact ? item.compactName : item.name;

              return (
                <button
                  key={item.id}
                  ref={item.compactName != null ? datasetButtonRef : undefined}
                  onClick={() => navigate(getPath(item.href))}
                  aria-current={active ? "page" : undefined}
                  aria-label={compact ? item.name : undefined}
                  title={item.name}
                  tabIndex={hideOnScroll ? -1 : undefined}
                  className={`relative flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 overflow-hidden px-0.5 py-1 transition-colors duration-200 ${
                    active ? "text-[#1F2753]" : "text-[#8a8d9c] hover:text-[#4a4d5f]"
                  }`}
                >
                  <span
                    className={`flex h-6 w-9 shrink-0 items-center justify-center rounded-full transition-colors ${
                      active ? "bg-[#e7e1d2]" : "bg-transparent"
                    }`}
                  >
                    <IconComponent className="h-5 w-5" strokeWidth={1.5} />
                  </span>
                  <span className={`w-full truncate text-center text-[10px] leading-tight ${active ? "font-semibold" : "font-medium"}`}>
                    {label}
                  </span>
                </button>
              );
            })}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export default BottomNavigation;
